import { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import { useAuth } from '../../auth/useAuth';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import './auth.css';

/** Landing page for the email-confirmation link. */
export function ConfirmEmailPage() {
  const [params] = useSearchParams();
  const [state, setState] = useState<'working' | 'ok' | 'failed'>('working');

  useEffect(() => {
    const userId = params.get('uid');
    const token = params.get('token');
    if (!userId || !token) {
      setState('failed');
      return;
    }
    api('/api/auth/confirm-email', { method: 'POST', body: JSON.stringify({ userId, token }) })
      .then(() => setState('ok'))
      .catch(() => setState('failed'));
  }, [params]);

  return (
    <div className="auth-page">
      <Card className="auth-card" title="Email confirmation">
        {state === 'working' ? <p>Confirming…</p> : null}
        {state === 'ok' ? (
          <div className="auth-info">
            Email confirmed. A club admin will now review your account — you&apos;ll get an email
            once you&apos;re approved.
          </div>
        ) : null}
        {state === 'failed' ? (
          <div className="auth-error">This confirmation link is invalid or has expired.</div>
        ) : null}
        <Link className="auth-inline-link" to="/login">
          Back to sign in
        </Link>
      </Card>
    </div>
  );
}

/** Shown after registration when the user tried to sign in before confirming. */
export function CheckYourEmailPage() {
  const location = useLocation();
  const email = (location.state as { email?: string } | null)?.email;
  return (
    <div className="auth-page">
      <Card className="auth-card" title="Confirm your email first">
        <div className="auth-info">
          {email ? (
            <>
              A confirmation link was sent to <strong>{email}</strong>.
            </>
          ) : (
            'A confirmation link was sent to your email address.'
          )}{' '}
          Follow it, then sign in again.
        </div>
        <Link className="auth-inline-link" to="/login">
          Back to sign in
        </Link>
      </Card>
    </div>
  );
}

/** Confirmed but not yet approved by an admin (also shown to signed-in Pending users). */
export function PendingApprovalPage() {
  const { user, logout } = useAuth();
  return (
    <div className="auth-page">
      <Card className="auth-card" title="Awaiting approval">
        <div className="auth-info">
          Your account is waiting for a club admin to approve it. You&apos;ll receive an email as
          soon as that happens.
        </div>
        {user ? (
          <Button variant="secondary" onClick={() => void logout()}>
            Sign out
          </Button>
        ) : (
          <Link className="auth-inline-link" to="/login">
            Back to sign in
          </Link>
        )}
      </Card>
    </div>
  );
}
